import React from "react";
import BrandGlyph from "../common/BrandGlyph";
import BrandLogo from "../common/BrandLogo";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store";
import { logout } from "../../features/auth/authSlice";
import { useTranslation } from "react-i18next";
import { useSidebarMenu } from "../../hooks/useSidebarMenu";
import SidebarItem from "./SidebarItem";
import { IoChevronBackOutline, IoChevronForwardOutline } from "react-icons/io5";
import { IoMdLogOut } from "react-icons/io";
interface SidebarProps {
  collapsed?: boolean;
  isOpen?: boolean;
  isMobile?: boolean;
  onToggle?: (collapsed: boolean) => void;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, isOpen = true, isMobile = false, onToggle, onClose }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { menuItems } = useSidebarMenu();

  const handleToggle = () => {
    if (onToggle) {
      onToggle(!collapsed);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
    if (onClose) onClose();
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  const handleLinkClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  return (
    <aside className={`sidebar-container ${collapsed ? "collapsed" : ""} ${isMobile ? "mobile" : ""} ${isOpen ? "open" : ""}`}>
      {/* Header */}
      <div className="sidebar-header d-flex align-items-center justify-content-between">
        <a href="/" className="brand-wrapper">{collapsed && !isMobile ? <BrandGlyph size={30} /> : <BrandLogo height={34} />}</a>

        {isMobile && (
          <div className="mobile-sidebar-quick-actions d-flex align-items-center gap-2">
            <div className="language-toggle-mini d-flex align-items-center">
              <span
                className={`lang-btn ${i18n.language === 'tr' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('tr')}
              >TR</span>
              <span className="separator mx-1">|</span>
              <span
                className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('en')}
              >EN</span>
            </div>
            <button className="mobile-logout-btn" onClick={handleLogout} title={t("logout")}>
              <IoMdLogOut />
            </button>
          </div>
        )}

        {!isMobile && (
          <button onClick={handleToggle} className="toggle-btn" aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
            {collapsed && !isMobile ? <IoChevronForwardOutline /> : <IoChevronBackOutline />}
          </button>
        )}
      </div>

      {/* Navigation */}
      <div className="sidebar-nav custom-scrollbar">
        <nav>
          {menuItems.map(item => (
            <div key={item.title} className="nav-item-wrapper">
              <SidebarItem item={item} collapsed={collapsed} isMobile={isMobile} onLinkClick={handleLinkClick} />
            </div>
          ))}
        </nav>
      </div>

      {/* Footer - Hidden on Mobile */}
      {!isMobile && (
        <div className="sidebar-footer">
          {/* User Profile Card */}
          {user && (
            <Link
              to="/profile"
              className={`user-profile-card text-decoration-none ${collapsed && !isMobile ? "collapsed" : ""}`}
              title={t("My Profile")}
              onClick={handleLinkClick}
            >
              <div className="user-avatar">{user.full_name?.charAt(0).toUpperCase() || "U"}</div>
              {!collapsed && (
                <div className="user-info">
                  <span className="user-name">{user.full_name}</span>
                  <span className="user-role">{user.roles?.[0]}</span>
                </div>
              )}
            </Link>
          )}

          {/* Logout */}
          <div onClick={handleLogout} className="footer-link " title={t("logout")}>
            <IoMdLogOut />
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
